import type { RefObject } from "react";
import { useEffect, useState, useTransition } from "react";

type UseResizeObservableArg = {
    observableElementRef: RefObject<HTMLElement | null>;
};

type UseResizeObservableResult = {
    width: number;
    height: number;
};

export const useResizeObservable = ({
    observableElementRef,
}: UseResizeObservableArg): UseResizeObservableResult => {
    const initialDimensionsCallback = (): UseResizeObservableResult => {
        const observableElement = observableElementRef.current;

        if (!observableElement) {
            return {
                width: 0,
                height: 0,
            };
        }

        return {
            width: observableElement.clientWidth,
            height: observableElement.clientHeight,
        };
    };

    const [dimensions, setDimensions] = useState<UseResizeObservableResult>(
        initialDimensionsCallback,
    );

    const [, startTransition] = useTransition();

    useEffect(() => {
        const observableElement = observableElementRef.current;
        if (!observableElement) return;

        const resizeObserver = new ResizeObserver((entries) => {
            startTransition(() => {
                const element = entries[0].target;
                if (element) {
                    const contentWidth = element.getBoundingClientRect().width;
                    const contentHeight =
                        element.getBoundingClientRect().height;

                    setDimensions({
                        width: contentWidth,
                        height: contentHeight,
                    });
                }
            });
        });

        resizeObserver.observe(observableElement);

        return () => {
            resizeObserver.disconnect();
        };
    }, [observableElementRef]);

    return dimensions;
};
